import { useState } from 'react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    practiceType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ds5mqtehdqnvnpj3hg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          phone: '',
          practiceType: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gray-bg">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h2 className="font-serif text-3xl font-bold text-navy-primary mb-8 text-center">
            Request a Strategy Call
          </h2>

          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-navy-primary mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200"
                  placeholder="Dr. John Smith"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-navy-primary mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200"
                  placeholder="john@practice.com"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="organization"
                className="block text-sm font-medium text-navy-primary mb-2"
              >
                Organization Name *
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200"
                placeholder="Your Practice Name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-navy-primary mb-2"
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label
                  htmlFor="practiceType"
                  className="block text-sm font-medium text-navy-primary mb-2"
                >
                  Practice Type *
                </label>
                <select
                  id="practiceType"
                  name="practiceType"
                  value={formData.practiceType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200 cursor-pointer"
                >
                  <option value="">Select practice type</option>
                  <option value="Private Practice">Private Practice</option>
                  <option value="Clinical Group">Clinical Group</option>
                  <option value="Multi-Provider Practice">Multi-Provider Practice</option>
                  <option value="Specialty Clinic">Specialty Clinic</option>
                  <option value="Physician-Led Organization">Physician-Led Organization</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-navy-primary mb-2"
              >
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gold transition-colors duration-200 resize-none"
                placeholder="Tell us about your practice and how we can help..."
              ></textarea>
              <p className="text-xs text-charcoal mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 bg-gold text-navy-primary text-base font-semibold rounded-lg hover:bg-gold-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Sending...
                </span>
              ) : (
                'Request Strategy Call'
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 text-center">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll respond within 24 business hours.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 text-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or contact us directly.
                </p>
              </div>
            )}
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-charcoal">
              <div className="flex items-center">
                <i className="ri-time-line text-gold mr-2"></i>
                We respond within 24 business hours
              </div>
              <div className="flex items-center">
                <i className="ri-lock-line text-gold mr-2"></i>
                Your information is secure
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-charcoal mb-4">Prefer to reach us directly?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="tel:8665195999"
              className="text-gold hover:text-gold-light font-medium transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              (866) 519-5999
            </a>
            <a
              href="mailto:admin@irsventures.com"
              className="text-gold hover:text-gold-light font-medium transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              admin@irsventures.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;